import API from "@app/services/api";
import { i18n } from "@app/i18n";
import { viewAsHeaders } from "@app/services/view-as";

/**
 * POST a request and save the file the API answers with — an Excel workbook or a CSV.
 *
 * The API service parses JSON; a file needs the raw response, so this is one of the few raw
 * `fetch()` calls, and it sends exactly what the service sends: the session cookie, the reader's
 * language (the file is worded in it) and the « voir comme » headers (the file must describe the
 * same perimeter as the screen).
 *
 * Returns the HTTP status: 403 is the read-only guard (a speciality being visited — files do not
 * leave it), which the caller words differently from a failure.
 */
export async function downloadPost(
  path: string,
  body: Record<string, unknown>,
  query: Record<string, string>,
  fallbackName: string,
): Promise<number> {
  const response = await fetch(API.getUrl(path, query), {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": i18n.locale || "fr",
      ...viewAsHeaders(),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) return response.status;
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const named = /filename="?([^";]+)"?/.exec(disposition)?.[1];
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = named ?? fallbackName;
  anchor.click();
  // After the click has been dispatched: revoking synchronously races the download.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
  return response.status;
}
